import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Shield, Sun, Plug, Zap, Wrench, Leaf, ArrowUpRight, MapPin, Phone, Mail, CheckCircle2 } from "lucide-react";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  {
    key: "alarmes",
    title: "Alarmes",
    label: "Sécurité 24/7",
    desc: "Vidéosurveillance, détection, télésurveillance NF & A2P.",
    icon: Shield,
    img: "https://images.pexels.com/photos/16423104/pexels-photo-16423104.jpeg?q=80&w=800",
  },
  {
    key: "solaire",
    title: "Solaire",
    label: "Photovoltaïque RGE",
    desc: "Autoconsommation, revente, batteries — installateur agréé.",
    icon: Sun,
    img: "https://images.unsplash.com/photo-1623331093892-587a082a7efa?q=80&w=800",
  },
  {
    key: "irve",
    title: "IRVE",
    label: "Bornes de recharge",
    desc: "Bornes 7 à 22 kW. Certification IRVE niveau expert.",
    icon: Plug,
    img: "https://images.unsplash.com/photo-1765272088009-100c96a4cd4e?q=80&w=800",
  },
  {
    key: "electricite",
    title: "Électricité Générale",
    label: "NF C 15-100",
    desc: "Neuf, rénovation, mise aux normes, tableau, prises.",
    icon: Zap,
    img: "https://images.unsplash.com/photo-1601462904263-f2fa0c851cb9?q=80&w=800",
  },
  {
    key: "sav",
    title: "SAV / Dépannage",
    label: "Petits travaux",
    desc: "Pose de lustre, ajout de prise, dépannage rapide — sans plans.",
    icon: Wrench,
    img: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?q=80&w=800",
  },
  {
    key: "audit_retrofit",
    title: "Audit Énergétique",
    label: "Audit & Rétrofit",
    desc: "Rapport expert : économies, mise aux normes, plan d'action chiffré. Forfait déduit des travaux.",
    icon: Leaf,
    img: "https://static.prod-images.emergentagent.com/jobs/c9f29e21-4a23-465b-92ea-90f5d1092b60/images/4dce7d55dad930185dc1d2e79bc3dc6e95ed9a197e0c018cf43f2c8b9247ac29.jpeg",
  },
];

export default function Home() {
  const nav = useNavigate();
  const [company, setCompany] = useState(null);
  useSEO({
    title: "YouElec · Électricien Perpignan 66 — Devis 24 h · Décennale",
    description:
      "Électricien à Perpignan et Pyrénées-Orientales (66). Devis gratuit en 24 h, dépannage 7j/7, alarmes, panneaux solaires, bornes IRVE. Norme NF C 15-100, garantie décennale Generali.",
    canonical: "https://nourlink.fr/",
  });
  useEffect(() => {
    axios
      .get(`${API}/company-public`)
      .then((r) => setCompany(r.data))
      .catch(() => {});
  }, []);

  return (
    <main className="pt-32 pb-24 hero-grad min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* HERO */}
        <section className="fade-up max-w-4xl mb-20">
          <div className="overline mb-6">YouElec · Maître électricien · Decennale RC</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tighter mb-8">
            L&apos;électricité,
            <br />
            <span className="text-gold-grad">au millimètre près.</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl leading-relaxed mb-10">
            Configurateur intelligent, analyse IA de vos plans, devis en
            quelques minutes. De l&apos;alarme au photovoltaïque, votre projet pris
            en main par des professionnels certifiés.
          </p>
          <div className="flex gap-4 flex-wrap">
            <button
              onClick={() => nav("/client")}
              className="btn-gold"
              data-testid="hero-cta-configure"
            >
              Démarrer mon devis →
            </button>
            <a
              href="#services"
              className="btn-ghost"
              data-testid="hero-cta-services"
            >
              Nos prestations
            </a>
          </div>
        </section>

        {/* BENTO 4 cards */}
        <section id="services" className="scroll-mt-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline mb-3">Nos 6 expertises</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                Choisissez votre service
              </h2>
            </div>
            <div className="hidden md:block text-sm text-slate-400 mono">
              6 · services
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  onClick={() => nav(`/client?service=${s.key}`)}
                  className="glass p-7 text-left group relative overflow-hidden transition-all duration-300 hover:-translate-y-1 fade-up"
                  style={{ animationDelay: `${i * 80}ms`, minHeight: 340 }}
                  data-testid={`bento-card-${s.key}`}
                >
                  {/* image */}
                  <div
                    className="absolute inset-0 opacity-15 group-hover:opacity-25 transition-opacity"
                    style={{
                      backgroundImage: `url(${s.img})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/70 to-transparent" />

                  <div className="relative flex flex-col h-full justify-between">
                    <div>
                      <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                        <Icon className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div className="overline mb-2">{s.label}</div>
                      <h3 className="font-display text-2xl font-bold tracking-tight mb-3 group-hover:text-[#D4AF37] transition-colors">
                        {s.title}
                      </h3>
                      <p className="text-sm text-slate-400 leading-relaxed">
                        {s.desc}
                      </p>
                    </div>
                    <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/5">
                      <span className="text-xs text-slate-500 mono">
                        0{i + 1}
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-[#D4AF37] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Trust strip */}
        <section className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            ["RGE", "Reconnu Garant de l'Environnement"],
            ["NF C 15-100", "Norme française électricité"],
            ["IRVE", "Installateur de bornes certifié"],
            ["Décennale", "Garantie 10 ans"],
          ].map(([t, d]) => (
            <div key={t} className="glass p-6">
              <div className="font-display text-2xl font-bold text-[#D4AF37] mb-1">
                {t}
              </div>
              <div className="text-xs text-slate-400">{d}</div>
            </div>
          ))}
        </section>

        {/* SEO content section : zone d'intervention + savoir-faire */}
        <section className="mt-28" id="zone">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="overline mb-3 flex items-center gap-2">
                <MapPin className="w-3 h-3" /> Zone d&apos;intervention
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-6">
                Électricien à Perpignan et dans toutes les Pyrénées-Orientales
              </h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                Basée au cœur de Perpignan, YouElec intervient dans un rayon
                de 30 km autour du chef-lieu du département 66. Nous nous
                déplaçons pour vos travaux d&apos;électricité générale,
                rénovation, mise aux normes NF C 15-100, dépannage urgent,
                installation d&apos;alarmes, panneaux solaires
                photovoltaïques et bornes de recharge IRVE pour véhicules
                électriques.
              </p>
              <p className="text-slate-300 leading-relaxed mb-4">
                Nous couvrons notamment&nbsp;: Perpignan, Canet-en-Roussillon,
                Saint-Estève, Cabestany, Bompas, Pia, Le Soler, Toulouges,
                Saleilles, Argelès-sur-Mer, Saint-Cyprien, Elne, Rivesaltes
                ainsi que toutes les communes limitrophes du littoral
                catalan.
              </p>
              <p className="text-slate-300 leading-relaxed">
                Chaque devis est détaillé ligne par ligne&nbsp;: fournitures,
                main d&apos;œuvre et déplacement clairement distincts. Nous
                respectons à la lettre la norme française NF C 15-100 sur
                toutes nos installations résidentielles et tertiaires, et
                vous bénéficiez de notre garantie décennale Generali (n° AR
                223 027) sur l&apos;ensemble de nos prestations.
              </p>
            </div>

            {/* Contact card */}
            <aside className="glass p-8 sticky top-32">
              <div className="overline mb-4">Contact direct</div>
              <h3 className="font-display text-2xl font-bold mb-6">
                Un projet ? Un dépannage ?
              </h3>
              <div className="space-y-4 text-sm">
                <a
                  href="tel:+33669548655"
                  className="flex items-center gap-3 text-slate-200 hover:text-[#D4AF37] transition-colors"
                  data-testid="contact-phone"
                >
                  <Phone className="w-4 h-4 text-[#D4AF37]" />
                  <span className="mono">06 69 54 86 55</span>
                </a>
                <a
                  href="mailto:youelec@nourlink.fr"
                  className="flex items-center gap-3 text-slate-200 hover:text-[#D4AF37] transition-colors"
                  data-testid="contact-email"
                >
                  <Mail className="w-4 h-4 text-[#D4AF37]" />
                  <span>youelec@nourlink.fr</span>
                </a>
                <div className="flex items-start gap-3 text-slate-400">
                  <MapPin className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                  <span>2 rue de la Petite Llosa<br />66000 Perpignan</span>
                </div>
              </div>
              <button
                onClick={() => nav("/client")}
                className="btn-gold w-full mt-8"
                data-testid="contact-cta-devis"
              >
                Demander un devis →
              </button>
              <p className="text-[10px] text-slate-500 mt-4 text-center mono">
                Réponse garantie sous 24&nbsp;h ouvrées
              </p>
            </aside>
          </div>
        </section>

        {/* FAQ section */}
        <section className="mt-28" id="faq">
          <div className="overline mb-3">Questions fréquentes</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-10">
            Tout ce que vous devez savoir avant de nous appeler
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                q: "Combien coûte un devis chez YouElec\u00A0?",
                a: "Le devis est entièrement gratuit et sans engagement. Nous étudions votre demande sous 24 h ouvrées et vous envoyons une proposition détaillée par email ou format papier.",
              },
              {
                q: "Intervenez-vous en urgence le week-end\u00A0?",
                a: "Oui, pour les dépannages électriques urgents (coupure totale, court-circuit, panne de tableau), nous intervenons 7j/7 sur Perpignan et alentours. Appelez le 06 69 54 86 55.",
              },
              {
                q: "Êtes-vous certifié RGE pour le solaire\u00A0?",
                a: "Oui, YouElec est qualifié RGE et IRVE pour vos installations photovoltaïques et bornes de recharge. Vous pouvez ainsi prétendre aux aides MaPrimeRénov\u2019 et au crédit d\u2019impôt.",
              },
              {
                q: "Vos installations sont-elles conformes NF C 15-100\u00A0?",
                a: "Toutes nos installations résidentielles et tertiaires respectent la dernière version de la norme française NF C 15-100, indispensable pour le passage du Consuel et la conformité de votre logement.",
              },
              {
                q: "Que comprend la garantie décennale\u00A0?",
                a: "Notre assurance Generali (n\u00B0 AR 223 027) couvre tous nos travaux d\u2019électricité, alarmes, solaire et IRVE pendant 10 ans à compter de la réception du chantier.",
              },
              {
                q: "Quels modes de paiement acceptez-vous\u00A0?",
                a: "Virement bancaire, chèque, espèces (jusqu\u2019à 1\u00A0000 €). Pour les chantiers de rénovation, un acompte de 30 % est demandé à la signature du devis.",
              },
            ].map((item) => (
              <article key={item.q} className="glass p-6">
                <div className="flex gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#D4AF37] shrink-0 mt-1" />
                  <div>
                    {/* Phase R5 #12 — Suppression dangerouslySetInnerHTML (XSS hardening) :
                        le contenu est désormais du texte plat JSX. Aucune perte UX :
                        entités HTML remplacées par caractères Unicode équivalents. */}
                    <h3 className="font-display font-bold text-base mb-2">{item.q}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{item.a}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Mentions légales mini-footer */}
        <footer className="mt-24 pt-10 border-t border-white/5 text-[11px] text-slate-500 mono">
          <p className="mb-2">
            YouElec — Auto-entrepreneur · 2 rue de la Petite Llosa, 66000
            Perpignan · SIRET&nbsp;:{" "}
            <span data-testid="footer-siret">
              {company?.siret || "à compléter (fiche Société)"}
            </span>
          </p>
          <p>
            Garantie décennale Generali n° AR 223 027 · TVA non applicable
            art. 293 B du CGI · © {new Date().getFullYear()} YouElec — Tous droits
            réservés
          </p>
        </footer>
      </div>
    </main>
  );
}
